/** AdminGlobalSearch - debounced cross-entity search box for the admin sidebar. */
import React, { useEffect, useRef, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../api/client';
import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';

interface SearchResult {
  type: 'owner' | 'user' | 'fleet' | 'transaction' | 'voucher';
  id: string;
  title: string;
  subtitle: string;
  route: string;
}

const TYPE_META: Record<SearchResult['type'], { label: string; icon: keyof typeof Ionicons.glyphMap }> = {
  owner: { label: 'Owners', icon: 'business' },
  user: { label: 'Customers', icon: 'people' },
  fleet: { label: 'Fleet Accounts', icon: 'car' },
  transaction: { label: 'Transactions', icon: 'card' },
  voucher: { label: 'Vouchers', icon: 'gift' },
};

const TYPE_ORDER: SearchResult['type'][] = ['owner', 'user', 'fleet', 'transaction', 'voucher'];

export default function AdminGlobalSearch({ onNavigate }: { onNavigate?: () => void }) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      setOpen(false);
      return;
    }
    setSearching(true);
    const mySeq = ++seq.current;
    timer.current = setTimeout(async () => {
      try {
        const data = await api<{ results: SearchResult[] }>(`/api/admin/search?q=${encodeURIComponent(term)}`);
        if (mySeq !== seq.current) return;
        setResults(data.results);
        setOpen(true);
      } catch {
        if (mySeq !== seq.current) return;
        setResults([]);
        setOpen(true);
      } finally {
        if (mySeq === seq.current) setSearching(false);
      }
    }, 300);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [query]);

  const selectResult = (r: SearchResult) => {
    setQuery('');
    setResults([]);
    setOpen(false);
    router.push(r.route as never);
    onNavigate?.();
  };

  const grouped = TYPE_ORDER
    .map(t => ({ type: t, items: results.filter(r => r.type === t) }))
    .filter(g => g.items.length > 0);

  return (
    <View style={styles.wrap}>
      <View style={styles.inputRow}>
        <Ionicons name="search" size={15} color={colors.textLight} />
        <TextInput
          style={styles.input}
          value={query}
          onChangeText={setQuery}
          placeholder="Search..."
          placeholderTextColor={colors.textLight}
          autoCapitalize="none"
          autoCorrect={false}
          accessibilityLabel="Global admin search"
        />
        {searching ? (
          <ActivityIndicator size="small" color={colors.textLight} />
        ) : query.length > 0 ? (
          <TouchableOpacity onPress={() => setQuery('')} hitSlop={8} accessibilityLabel="Clear search">
            <Ionicons name="close-circle" size={15} color={colors.textLight} />
          </TouchableOpacity>
        ) : null}
      </View>
      {open && (
        <View style={styles.results}>
          {grouped.length === 0 && !searching ? (
            <Text style={styles.emptyText}>No results</Text>
          ) : (
            grouped.map(group => (
              <View key={group.type}>
                <Text style={styles.groupTitle}>{TYPE_META[group.type].label}</Text>
                {group.items.map(r => (
                  <TouchableOpacity
                    key={`${r.type}-${r.id}`}
                    style={styles.resultRow}
                    onPress={() => selectResult(r)}
                    accessibilityRole="button"
                    accessibilityLabel={`${TYPE_META[r.type].label}: ${r.title}`}
                  >
                    <Ionicons name={TYPE_META[r.type].icon} size={14} color={colors.textLight} style={{ marginTop: 2 }} />
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={styles.resultTitle} numberOfLines={1}>{r.title}</Text>
                      <Text style={styles.resultSub} numberOfLines={1}>{r.subtitle}</Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            ))
          )}
        </View>
      )}
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  wrap: {
    paddingHorizontal: spacing.sm,
    marginBottom: spacing.sm,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: c.background,
    borderWidth: 1,
    borderColor: c.borderLight,
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing.sm,
    height: 34,
  },
  input: {
    flex: 1,
    fontSize: fontSize.sm,
    color: c.text,
    paddingVertical: 0,
  },
  results: {
    marginTop: 4,
    backgroundColor: c.background,
    borderWidth: 1,
    borderColor: c.borderLight,
    borderRadius: borderRadius.sm,
    paddingVertical: 4,
    maxHeight: 320,
    overflow: 'hidden',
  },
  emptyText: {
    fontSize: fontSize.xs,
    color: c.textLight,
    textAlign: 'center',
    paddingVertical: spacing.sm,
  },
  groupTitle: {
    fontSize: 10,
    fontWeight: fontWeight.semibold,
    color: c.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingHorizontal: spacing.sm,
    paddingTop: 6,
    paddingBottom: 2,
  },
  resultRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
  },
  resultTitle: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.medium,
    color: c.text,
  },
  resultSub: {
    fontSize: 10,
    color: c.textLight,
    marginTop: 1,
  },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
